import { apiDelete, apiGet, apiPatch, apiPost, client } from '../client'
import { WorkAuthRequired } from './jobs'

export type DegreeProgram =
  | 'ASSOCIATE' | 'BACHELOR_OF_ARTS' | 'BACHELOR_OF_SCIENCE' | 'BACHELOR_OF_ENGINEERING' | 'BACHELOR_OF_FINE_ARTS'
  | 'BACHELOR_OF_BUSINESS_ADMINISTRATION' | 'MASTER_OF_ARTS' | 'MASTER_OF_SCIENCE' | 'MASTER_OF_ENGINEERING'
  | 'MASTER_OF_BUSINESS_ADMINISTRATION' | 'MASTER_OF_FINE_ARTS' | 'MASTER_OF_PUBLIC_HEALTH' | 'MASTER_OF_PUBLIC_POLICY'
  | 'MASTER_OF_SOCIAL_WORK' | 'MASTER_OF_EDUCATION' | 'MASTER_OF_LAWS' | 'JURIS_DOCTOR' | 'DOCTOR_OF_MEDICINE'
  | 'DOCTOR_OF_DENTAL_SURGERY' | 'DOCTOR_OF_PHARMACY' | 'DOCTOR_OF_PHILOSOPHY' | 'DOCTOR_OF_EDUCATION'
  | 'DOCTOR_OF_NURSING_PRACTICE' | 'POSTDOCTORAL' | 'CERTIFICATE' | 'OTHER'

export interface StudentEducationEntry {
  createdAt?: string
  degree?: string
  description?: string
  endDate?: string
  gpa?: number
  id: string
  institution?: string
  isCurrent?: boolean
  major?: string
  minor?: string
  startDate?: string
  studentId?: string
}

export interface StudentExperienceEntry {
  company?: string
  createdAt?: string
  description?: string
  employmentType?: string
  endDate?: string
  id: string
  isCurrent?: boolean
  isRemote?: boolean
  location?: string
  startDate?: string
  studentId?: string
  title?: string
}

export type WorkAuthApprovalStatus = 'APPROVED_RECEIVED' | 'IN_PROGRESS' | 'SUBMITTED'

export type WorkArrangement = 'ON_SITE' | 'HYBRID' | 'REMOTE' | 'OPEN_TO_ANY'

export type Industry =
  | 'TECHNOLOGY' | 'HEALTHCARE' | 'FINANCE' | 'EDUCATION' | 'MANUFACTURING' | 'RETAIL' | 'CONSULTING'
  | 'MEDIA_AND_ENTERTAINMENT' | 'GOVERNMENT' | 'NON_PROFIT' | 'REAL_ESTATE' | 'LEGAL' | 'ENERGY'
  | 'TRANSPORTATION' | 'HOSPITALITY' | 'AGRICULTURE' | 'CONSTRUCTION' | 'TELECOMMUNICATIONS'
  | 'PHARMACEUTICALS' | 'AEROSPACE_AND_DEFENSE' | 'OTHER'

/** The narrower set of statuses students choose from - distinct from the
 * broader WorkAuthRequired list employers use to filter jobs. */
export type StudentWorkAuthStatus = 'CPT' | 'OPT' | 'STEM_OPT' | 'H1B'

/** Unstructured resume content preserved verbatim from the pre-rebuild
 * Firebase system for migrated students - the source data never had real
 * structured fields (institution/company/dates), only free text, so only
 * `text` is reliably populated. Read-only: not writable via PATCH. */
export interface LegacyResumeEntry {
  content?: string
  text: string
}

export interface StudentVolunteerEntry {
  createdAt?: string
  description?: string
  endDate?: string
  id: string
  isCurrent?: boolean
  location?: string
  organization?: string
  startDate?: string
  studentId?: string
  title?: string
}

export interface StudentProfileData {
  _count?: { applications?: number; savedJobs?: number }
  avatarUrl?: string | null
  bio?: string
  degreeProgram?: DegreeProgram
  education: StudentEducationEntry[]
  expectedGraduation?: string
  experience: StudentExperienceEntry[]
  firstName: string
  gpa?: number
  id: string
  industriesOfInterest?: Industry[]
  institution?: { id?: string; logoUrl?: string | null; name: string; type?: string }
  interests?: string[]
  lastName: string
  legacyEducationRaw?: LegacyResumeEntry[] | null
  legacyExperienceRaw?: LegacyResumeEntry[] | null
  linkedinUrl?: string
  location?: string
  major?: string
  onboardingStatus?: string
  openToRelocation?: boolean | null
  phoneNumber?: string
  portfolioUrl?: string
  preferredEmploymentTypes?: string[]
  preferredLocations?: string[]
  preferredWorkArrangement?: WorkArrangement | null
  resumeUrl?: string | null
  skills?: string[]
  targetRoles?: string[]
  userId: string
  verificationStatus?: string
  visaEndDate?: string | null
  visaStartDate?: string | null
  volunteerEngagements?: StudentVolunteerEntry[]
  workAuthApprovalDate?: string
  workAuthApprovalStatus?: WorkAuthApprovalStatus
  workAuthorization?: WorkAuthRequired
}

export interface UpdateStudentProfilePayload {
  avatarUrl?: string
  bio?: string
  degreeProgram?: DegreeProgram
  expectedGraduation?: string
  firstName?: string
  gpa?: number
  industriesOfInterest?: Industry[]
  interests?: string[]
  lastName?: string
  linkedinUrl?: string
  location?: string
  major?: string
  openToRelocation?: boolean
  phoneNumber?: string
  portfolioUrl?: string
  preferredEmploymentTypes?: string[]
  preferredLocations?: string[]
  preferredWorkArrangement?: WorkArrangement
  resumeUrl?: string
  skills?: string[]
  targetRoles?: string[]
  visaEndDate?: string
  visaStartDate?: string
  workAuthApprovalDate?: string
  workAuthApprovalStatus?: WorkAuthApprovalStatus
  workAuthorization?: WorkAuthRequired
}

export interface VolunteerPayload {
  description?: string
  endDate?: string
  isCurrent?: boolean
  location?: string
  organization?: string
  startDate?: string
  title?: string
}

export interface EducationPayload {
  degree?: string
  description?: string
  endDate?: string
  gpa?: number
  institution?: string
  isCurrent?: boolean
  major?: string
  minor?: string
  startDate?: string
}

export interface ExperiencePayload {
  company?: string
  description?: string
  employmentType?: string
  endDate?: string
  isCurrent?: boolean
  isRemote?: boolean
  location?: string
  startDate?: string
  title?: string
}

export const getMyStudentProfile = () => apiGet<StudentProfileData>('/student/profile')

export const updateMyStudentProfile = (payload: UpdateStudentProfilePayload) => apiPatch<StudentProfileData>('/student/profile', payload)

export const addEducation = (payload: EducationPayload) => apiPost<StudentEducationEntry>('/student/education', payload)

export const updateEducation = (id: string, payload: EducationPayload) => apiPatch<StudentEducationEntry>(`/student/education/${id}`, payload)

export const deleteEducation = (id: string) => apiDelete<void>(`/student/education/${id}`)

export const addExperience = (payload: ExperiencePayload) => apiPost<StudentExperienceEntry>('/student/experience', payload)

export const updateExperience = (id: string, payload: ExperiencePayload) => apiPatch<StudentExperienceEntry>(`/student/experience/${id}`, payload)

export const deleteExperience = (id: string) => apiDelete<void>(`/student/experience/${id}`)

export const addVolunteerEngagement = (payload: VolunteerPayload) => apiPost<StudentVolunteerEntry>('/student/volunteer-engagement', payload)

export const updateVolunteerEngagement = (id: string, payload: VolunteerPayload) => apiPatch<StudentVolunteerEntry>(`/student/volunteer-engagement/${id}`, payload)

export const deleteVolunteerEngagement = (id: string) => apiDelete<void>(`/student/volunteer-engagement/${id}`)

/** Streams a raw PDF binary, not the standard `{success, data}` envelope -
 * bypasses apiGet's unwrap and hits the axios client directly. */
export const exportResume = async (): Promise<Blob> => {
  const response = await client.get('/student/profile/export-resume', { responseType: 'blob' })
  return response.data as Blob
}

export const COMPLETION_FIELDS: (keyof StudentProfileData)[] = [
  'firstName', 'lastName', 'bio', 'phoneNumber', 'location', 'major',
  'degreeProgram', 'workAuthorization', 'expectedGraduation', 'linkedinUrl', 'resumeUrl',
]

export function calculateStudentProfileCompletion(profile: StudentProfileData | null): number {
  if (!profile) return 0
  const scalarFilled = COMPLETION_FIELDS.filter((field) => {
    const value = profile[field]
    return value !== null && value !== undefined && value !== ''
  }).length
  const listFilled = [profile.skills, profile.education, profile.experience].filter((list) => (list?.length ?? 0) > 0).length
  return Math.round(((scalarFilled + listFilled) / (COMPLETION_FIELDS.length + 3)) * 100)
}
